// Claude: Industry admin page — inline add input appears below parent node 27-02-2026
import React, { useEffect, useState, useCallback } from "react";
import { Tree, Input as AntInput, ConfigProvider, Modal } from "antd";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import {
  AiOutlinePlus,
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlineCheck,
  AiOutlineClose,
} from "react-icons/ai";
import { AdminAPI } from "../../Apis/adminAPI";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { errorHandler } from "../../Utils/handler";
import { useNavigate } from "react-router-dom";

type Props = {};

// addingParentId:
//   undefined → not in "add" mode
//   null      → adding a root Industry node (phantom appended to root list)
//   string    → adding a child of this node's _id (phantom appended to that node's children)
type AddingMode = string | null | undefined;

const PHANTOM_KEY = "__new_node__";

const Industry = (props: Props) => {
  const navigate = useNavigate();
  const [nodes, setNodes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [addingParentId, setAddingParentId] = useState<AddingMode>(undefined);
  const [newNodeName, setNewNodeName] = useState("");
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  // Claude: controlled expand so parent auto-expands when + is clicked 20-02-2026
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  // Claude: warn before rename/delete — prompt user to check Stats first 15-04-2026
  const [warnPending, setWarnPending] = useState<(() => void) | null>(null);
  const { success, error } = useToastWrapper();

  const fetchTree = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AdminAPI.getIndustryTree();
      const flat: any[] = Array.isArray(res) ? res : (res?.data ?? []);
      setNodes(flat);
      // expand all nodes by default after fetch
      setExpandedKeys(flat.map((n) => n._id));
    } catch (err: any) {
      error(errorHandler(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  const handleAddNode = async () => {
    if (!newNodeName.trim()) {
      error("Please enter a name");
      return;
    }
    // Claude: duplicate check — same name (case-insensitive) under same parent 01-03-2026
    const parentId = addingParentId ?? null;
    const isDupe = nodes.some(
      (n) =>
        n.name.trim().toLowerCase() === newNodeName.trim().toLowerCase() &&
        (n.parentId ?? null) === parentId
    );
    if (isDupe) {
      error(`"${newNodeName.trim()}" already exists here`);
      return;
    }
    try {
      const payload: any = { name: newNodeName.trim() };
      if (addingParentId) payload.parentId = addingParentId;
      await AdminAPI.createIndustryNode(payload);
      success("Node created", "");
      setNewNodeName("");
      setAddingParentId(undefined);
      fetchTree();
    } catch (err: any) {
      error(errorHandler(err));
    }
  };

  const handleCancelAdd = () => {
    setAddingParentId(undefined);
    setNewNodeName("");
  };

  const handleRenameNode = async (id: string) => {
    if (!editingName.trim()) {
      error("Please enter a name");
      return;
    }
    // Claude: duplicate check on rename — same name under same parent, excluding self 01-03-2026
    const self = nodes.find((n) => n._id === id);
    const isDupe = nodes.some(
      (n) =>
        n._id !== id &&
        n.name.trim().toLowerCase() === editingName.trim().toLowerCase() &&
        (n.parentId ?? null) === (self?.parentId ?? null)
    );
    if (isDupe) {
      error(`"${editingName.trim()}" already exists here`);
      return;
    }
    try {
      await AdminAPI.updateIndustryNode(id, { name: editingName.trim() });
      success("Node renamed", "");
      setEditingId(null);
      fetchTree();
    } catch (err: any) {
      error(errorHandler(err));
    }
  };

  const handleDeleteNode = async (id: string, name: string) => {
    if (!window.confirm(`Delete "${name}" and all its children?`)) return;
    try {
      await AdminAPI.deleteIndustryNode(id);
      success("Node deleted", "");
      if (selectedNodeId === id) setSelectedNodeId(null);
      fetchTree();
    } catch (err: any) {
      error(errorHandler(err));
    }
  };

  // Claude: nowrap + minWidth prevents tree from scrunching node titles 20-02-2026
  const nodeTitleStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 4,
    whiteSpace: "nowrap",
    minWidth: 0,
    fontSize: 18,
    fontFamily: "inherit",
  };

  // Inline input rendered as a tree node title — appears below the parent
  const renderPhantomTitle = (): React.ReactNode => (
    <span style={{ ...nodeTitleStyle, gap: 6 }} onClick={(e) => e.stopPropagation()}>
      <AntInput
        size="small"
        placeholder="Node name"
        value={newNodeName}
        onChange={(e) => setNewNodeName(e.target.value)}
        onPressEnter={handleAddNode}
        autoFocus
        style={{ width: 260 }}
      />
      {/* Claude: hover colors consistent with admin button conventions 01-03-2026 */}
      <Button
        size="sm"
        background="primary.main"
        color="white"
        _hover={{ background: "#2E7D32", color: "white" }}
        onClick={(e) => { e.stopPropagation(); handleAddNode(); }}
      >
        <AiOutlineCheck />
      </Button>
      <Button
        size="sm"
        variant="outline"
        _hover={{ background: "#C62828", color: "white", borderColor: "#C62828" }}
        onClick={(e) => { e.stopPropagation(); handleCancelAdd(); }}
      >
        <AiOutlineClose />
      </Button>
    </span>
  );

  // Normal node title with +/edit/delete buttons
  const renderNodeTitle = (node: any): React.ReactNode => {
    if (editingId === node._id) {
      return (
        <span style={{ ...nodeTitleStyle, gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <AntInput
            size="small"
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onPressEnter={() => handleRenameNode(node._id)}
            autoFocus
            style={{ width: 260 }}
          />
          <Button
            size="sm"
            background="primary.main"
            color="white"
            _hover={{ background: "#2E7D32", color: "white" }}
            onClick={(e) => { e.stopPropagation(); handleRenameNode(node._id); }}
          >
            <AiOutlineCheck />
          </Button>
          <Button
            size="sm"
            variant="outline"
            _hover={{ background: "#C62828", color: "white", borderColor: "#C62828" }}
            onClick={(e) => { e.stopPropagation(); setEditingId(null); }}
          >
            <AiOutlineClose />
          </Button>
        </span>
      );
    }
    return (
      <span style={nodeTitleStyle}>
        <span style={{ fontSize: 18, fontWeight: node.parentId ? 400 : 600 }}>{node.name}</span>
        {/* + button on every node to add a child directly below it */}
        <Button
          size="xs"
          variant="ghost"
          title="Add child"
          onClick={(e) => {
            e.stopPropagation();
            setAddingParentId(node._id);
            setNewNodeName("");
            setEditingId(null);
            setExpandedKeys((prev) =>
              prev.includes(node._id) ? prev : [...prev, node._id]
            );
          }}
        >
          <AiOutlinePlus size={14} />
        </Button>
        <Button
          size="xs"
          variant="ghost"
          title="Rename"
          onClick={(e) => {
            e.stopPropagation();
            setWarnPending(() => () => {
              setEditingId(node._id);
              setEditingName(node.name);
              setAddingParentId(undefined);
            });
          }}
        >
          <AiOutlineEdit size={14} />
        </Button>
        <Button
          size="xs"
          variant="ghost"
          colorScheme="red"
          title="Delete"
          onClick={(e) => {
            e.stopPropagation();
            setWarnPending(() => () => handleDeleteNode(node._id, node.name));
          }}
        >
          <AiOutlineDelete size={14} />
        </Button>
      </span>
    );
  };

  // Claude: phantom node injected as child of the clicked parent — input appears in-place 20-02-2026
  const buildTreeData = (flatNodes: any[]): any[] => {
    const nodeMap: Record<string, any> = {};
    flatNodes.forEach((n) => {
      nodeMap[n._id] = {
        ...n,
        key: n._id,
        title: renderNodeTitle(n),
        children: [],
      };
    });

    const roots: any[] = [];
    let rootIndex = 0;
    flatNodes.forEach((n) => {
      if (!n.parentId) {
        // Claude: top margin on every root node except the first creates a clear visual break
        // between industries, so showLine's sibling connector is not mistaken for a parent-child link 20-02-2026
        if (rootIndex > 0) nodeMap[n._id].style = { marginTop: 16 };
        roots.push(nodeMap[n._id]);
        rootIndex++;
      } else if (nodeMap[n.parentId]) {
        nodeMap[n.parentId].children.push(nodeMap[n._id]);
      }
    });

    // Inject phantom input node at the right position
    if (addingParentId !== undefined) {
      const phantomNode = {
        key: PHANTOM_KEY,
        title: renderPhantomTitle(),
        children: [],
        isLeaf: true,
        selectable: false,
      };
      if (addingParentId === null) {
        // Claude: root-level add — prepend so input appears at top 01-03-2026
        roots.unshift(phantomNode);
      } else if (nodeMap[addingParentId]) {
        // Claude: child add — prepend so input appears at top of children 01-03-2026
        nodeMap[addingParentId].children.unshift(phantomNode);
      }
    }

    return roots;
  };

  const treeData = buildTreeData(nodes);
  const selectedNode = nodes.find((n) => n._id === selectedNodeId);
  const rootNodes = nodes.filter((n) => !n.parentId);

  return (
    <div className="p-4">
      {/* Claude: Stats button — navigate to industry stats page 13-03-2026 */}
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="2xl" fontWeight={600}>Industry</Text>
        <Button
          size="sm"
          background="primary.main"
          color="white"
          _hover={{ background: "#2E7D32", color: "white" }}
          onClick={() => navigate("/admin/industry-stats")}
        >
          Stats
        </Button>
      </Flex>

      <Flex gap={6} align="flex-start">
        {/* Left panel — Tree */}
        <Box
          flex="1"
          minW="420px"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          p={4}
          minH="400px"
          overflowX="auto"
        >
          <Flex mb={3}>
            <Button
              size="sm"
              background="primary.main"
              color="white"
              _hover={{ background: "#2E7D32", color: "white" }}
              leftIcon={<AiOutlinePlus />}
              onClick={() => {
                setAddingParentId(null);
                setNewNodeName("");
                setEditingId(null);
              }}
            >
              Add Industry
            </Button>
          </Flex>

          {treeData.length === 0 && !loading ? (
            <Text fontSize="md" color="gray.500" mt={4}>
              No industry nodes yet. Click "Add Industry" to get started.
            </Text>
          ) : (
            <ConfigProvider theme={{ token: { fontSize: 18, fontFamily: 'inherit' }, components: { Tree: { titleHeight: 40 } } }}>{/* Claude: token override — only reliable way to change font size in AntD v5 20-02-2026 */}
              <Tree
                showLine={{ showLeafIcon: false }}
                treeData={treeData}
                expandedKeys={expandedKeys}
                onExpand={(keys) => setExpandedKeys(keys as string[])}
                selectedKeys={selectedNodeId ? [selectedNodeId] : []}
                onSelect={(keys) => {
                  const key = keys[0] as string;
                  if (key === PHANTOM_KEY) return;
                  setSelectedNodeId(key || null);
                }}
              />
            </ConfigProvider>
          )}
        </Box>

        {/* Right panel — Details */}
        <Box
          w="300px"
          flexShrink={0}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          p={4}
          minH="400px"
        >
          <Text fontWeight={600} mb={3} fontSize="lg">
            Details
          </Text>
          {selectedNode ? (
            <Box>
              <Text fontWeight={500} fontSize="md">{selectedNode.name}</Text>
              <Text fontSize="md" color="gray.500" mt={1}>
                {selectedNode.parentId
                  ? "Category / Sub-category node"
                  : "Top-level Industry"}
              </Text>
              {!selectedNode.parentId && (
                <Box
                  mt={4}
                  p={3}
                  bg="blue.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="blue.200"
                >
                  <Text fontSize="sm" color="blue.700">
                    To assign sellers to this industry, go to the{" "}
                    <strong>Sellers</strong> page, select sellers, and
                    click <strong>"Assign Industry"</strong>.
                  </Text>
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              <Text fontSize="md" color="gray.500">
                Select a node in the tree to see details.
              </Text>
              <Box mt={4}>
                <Text fontSize="md" fontWeight={500} mb={2}>
                  Summary
                </Text>
                <Text fontSize="md" color="gray.600">
                  Total industries (root nodes):{" "}
                  <strong>{rootNodes.length}</strong>
                </Text>
                <Text fontSize="md" color="gray.600">
                  Total nodes: <strong>{nodes.length}</strong>
                </Text>
              </Box>
            </Box>
          )}
        </Box>
      </Flex>

      {/* Claude: warn before rename/delete — prompt user to check Stats first 15-04-2026 */}
      <Modal
        open={!!warnPending}
        onCancel={() => setWarnPending(null)}
        onOk={() => { warnPending?.(); setWarnPending(null); }}
        okText="Continue"
        cancelText="Cancel"
        title="Check Stats Before Editing"
        centered
      >
        <Text fontSize="sm">
          Check <strong>Stats</strong> on how many sellers and buyers are associated with this industry before editing.
        </Text>
      </Modal>
    </div>
  );
};

export default Industry;
